import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  Transition,
} from '@headlessui/react';
import {
  BreakType,
  DaySequence,
  DaySequenceChangeInput,
  Job,
} from '@core/generated/gql-types';
import {
  millisecondsToMinutes,
  minutesToMilliseconds,
} from '@core/util/datetime.util';
import { formatTimeForTimePicker } from '@core/util/jobTimezone.util';
import {
  formatLoggedTime,
  formatTime,
  formatBreakTime,
  getTimestampFromTime,
} from '@/utils/utils';
import TimePicker from '@/components/shared/TimePicker';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import UpdateTimesheetModal from './UpdateTimesheetModal';
import Button from '@/components/Button';
import { jobDaysOvernight } from '@core/util/jobScheduleBounds.util';
import { parseScheduleDateLocal } from '@/utils/createJobShiftValidation.util';

import { TimesheetEditAction } from '@core/util/timesheetPortalActions.util';

/**
 * Editable form for adjusting timesheet clock-in, clock-out, and break.
 *
 * - Client (`editAction: 'propose'`): opens confirmation, then `proposeDaySequenceChanges`.
 * - Tenant dispute (`editAction: 'resolve'`): opens confirmation, then `resolveDaySequenceDisputes`.
 *
 * Approve does not use this modal — that flow goes straight to UpdateTimesheetModal.
 */
interface EditTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  updateTimesheet: DaySequence | null;
  editAction: TimesheetEditAction;
  onResolveTimesheetDispute: (
    daySequenceChange: DaySequenceChangeInput,
  ) => Promise<void>;
  onProposeTimesheetChanges: (
    daySequenceChange: DaySequenceChangeInput,
  ) => Promise<void>;
}

function calendarDayFromSequenceTimestamp(
  msString?: string | null,
  fallbackDateISO?: string | null,
): Date {
  if (msString) {
    const n = Number(msString);
    if (!Number.isNaN(n)) {
      return dayjs(n).startOf('day').toDate();
    }
  }
  if (fallbackDateISO) {
    return parseScheduleDateLocal(fallbackDateISO).toDate();
  }
  return dayjs().startOf('day').toDate();
}

const EditTimesheetModal = ({
  isOpen,
  onClose,
  job,
  updateTimesheet,
  editAction,
  onResolveTimesheetDispute,
  onProposeTimesheetChanges,
}: EditTimesheetModalProps) => {
  const [
    isResolveTimesheetConfirmationOpen,
    setIsResolveTimesheetConfirmationOpen,
  ] = useState(false);

  const isOvernightShift = useMemo(
    () => jobDaysOvernight(job.days),
    [job.days?.startTime, job.days?.endTime],
  );

  const getTimePickerValue = useCallback(
    (unixMs?: string | null, scheduleClockTime?: string | null): string =>
      formatTimeForTimePicker(unixMs, scheduleClockTime, job),
    [job],
  );

  const [clockInDate, setClockInDate] = useState<Date>(() =>
    dayjs().startOf('day').toDate(),
  );
  const [clockOutDate, setClockOutDate] = useState<Date>(() =>
    dayjs().startOf('day').toDate(),
  );

  const [clockOutTime, setClockOutTime] = useState<string>(() =>
    getTimePickerValue(updateTimesheet?.clockOutTime, job.days?.endTime),
  );

  const [clockInTime, setClockInTime] = useState<string>(() =>
    getTimePickerValue(updateTimesheet?.clockInTime, job.days?.startTime),
  );

  const [breakTimeMinutes, setBreakTimeMinutes] = useState<string>('0');

  const jobCalendarBounds = useMemo(() => {
    const startMs = job.startDate ? Number(job.startDate) : NaN;
    const endMs = job.endDate ? Number(job.endDate) : NaN;
    const min = !Number.isNaN(startMs)
      ? dayjs(startMs).startOf('day').toDate()
      : undefined;
    const max = !Number.isNaN(endMs)
      ? dayjs(endMs).startOf('day').toDate()
      : undefined;
    return { min, max };
  }, [job.startDate, job.endDate]);

  const clockOutDateMax = useMemo(() => {
    if (!jobCalendarBounds.max) return undefined;
    return dayjs(jobCalendarBounds.max)
      .add(isOvernightShift ? 1 : 0, 'day')
      .toDate();
  }, [jobCalendarBounds.max, isOvernightShift]);

  const clockInDateISO = useMemo(
    () => dayjs(clockInDate).format('YYYY-MM-DD'),
    [clockInDate],
  );
  const clockOutDateISO = useMemo(
    () => dayjs(clockOutDate).format('YYYY-MM-DD'),
    [clockOutDate],
  );

  const clockInTimestamp = useMemo(() => {
    if (!updateTimesheet?.id) return '';
    return getTimestampFromTime(clockInDateISO, clockInTime);
  }, [updateTimesheet?.id, clockInDateISO, clockInTime]);

  const clockOutTimestamp = useMemo(() => {
    if (!updateTimesheet?.id) return '';
    return getTimestampFromTime(clockOutDateISO, clockOutTime);
  }, [updateTimesheet?.id, clockOutDateISO, clockOutTime]);

  const handleClockInDateChange = (nextIn: Date) => {
    setClockInDate(nextIn);
    const inTs = getTimestampFromTime(
      dayjs(nextIn).format('YYYY-MM-DD'),
      clockInTime,
    );
    let nextOut = clockOutDate;
    if (dayjs(nextOut).isBefore(dayjs(nextIn), 'day')) {
      nextOut = nextIn;
    }
    const outTs = getTimestampFromTime(
      dayjs(nextOut).format('YYYY-MM-DD'),
      clockOutTime,
    );
    if (Number(outTs) <= Number(inTs) && isOvernightShift) {
      nextOut = dayjs(nextIn).add(1, 'day').startOf('day').toDate();
    }
    setClockOutDate(nextOut);
  };

  const timesheetDuration = useMemo(
    () =>
      formatLoggedTime(
        clockInTimestamp,
        clockOutTimestamp,
        minutesToMilliseconds(parseInt(breakTimeMinutes, 10) || 0),
      ),
    [clockInTimestamp, clockOutTimestamp, breakTimeMinutes],
  );

  const isInvalidTimeRange = useMemo(
    () =>
      Boolean(
        clockInTimestamp &&
          clockOutTimestamp &&
          Number(clockOutTimestamp) <= Number(clockInTimestamp),
      ),
    [clockInTimestamp, clockOutTimestamp],
  );

  const invalidInput = useMemo(
    () => isInvalidTimeRange || timesheetDuration === '0 hrs',
    [isInvalidTimeRange, timesheetDuration],
  );

  const isBreakTooLong = useMemo(
    () => !isInvalidTimeRange && timesheetDuration === '0 hrs',
    [isInvalidTimeRange, timesheetDuration],
  );

  useEffect(() => {
    if (!isOpen || !isOvernightShift || !updateTimesheet?.id) return;
    const inTs = Number(getTimestampFromTime(clockInDateISO, clockInTime));
    const outTs = Number(getTimestampFromTime(clockOutDateISO, clockOutTime));
    if (
      Number.isNaN(inTs) ||
      Number.isNaN(outTs) ||
      outTs > inTs ||
      !dayjs(clockOutDate).isSame(dayjs(clockInDate), 'day')
    ) {
      return;
    }
    setClockOutDate(dayjs(clockInDate).add(1, 'day').startOf('day').toDate());
  }, [
    isOpen,
    isOvernightShift,
    updateTimesheet?.id,
    clockInDate,
    clockOutDate,
    clockInDateISO,
    clockOutDateISO,
    clockInTime,
    clockOutTime,
  ]);

  const resolvedTimesheetInput = useMemo((): DaySequenceChangeInput => {
    const id = updateTimesheet?.id || '';
    const breakMinutes = parseInt(breakTimeMinutes, 10) || 0;
    const proposedBreaks =
      breakMinutes > 0 && id
        ? [
            {
              daySequenceId: id,
              duration: String(minutesToMilliseconds(breakMinutes)),
              type: BreakType.Unpaid,
            },
          ]
        : [];

    if (!id) {
      return {
        id: '',
        clockInTimestamp: '',
        clockOutTimestamp: '',
        proposedBreaks,
      };
    }

    return {
      id,
      clockInTimestamp: getTimestampFromTime(
        dayjs(clockInDate).format('YYYY-MM-DD'),
        clockInTime,
      ),
      clockOutTimestamp: getTimestampFromTime(
        dayjs(clockOutDate).format('YYYY-MM-DD'),
        clockOutTime,
      ),
      proposedBreaks,
    };
  }, [
    updateTimesheet?.id,
    clockInDate,
    clockOutDate,
    clockInTime,
    clockOutTime,
    breakTimeMinutes,
  ]);

  useEffect(() => {
    if (isOpen && updateTimesheet) {
      const fallbackISO = updateTimesheet.startDateISO || null;
      const inMs =
        updateTimesheet.clockInTime ||
        updateTimesheet.proposedClockInTime ||
        null;
      const outMs =
        updateTimesheet.clockOutTime ||
        updateTimesheet.proposedClockOutTime ||
        null;

      const nextInDate = calendarDayFromSequenceTimestamp(inMs, fallbackISO);
      setClockInDate(nextInDate);

      let nextOutDate = calendarDayFromSequenceTimestamp(outMs, fallbackISO);
      if (!outMs && isOvernightShift) {
        nextOutDate = dayjs(nextInDate).add(1, 'day').startOf('day').toDate();
      }
      if (dayjs(nextOutDate).isBefore(dayjs(nextInDate), 'day')) {
        nextOutDate = nextInDate;
      }
      setClockOutDate(nextOutDate);

      setClockInTime(
        getTimePickerValue(
          updateTimesheet.clockInTime || updateTimesheet.proposedClockInTime,
          job.days?.startTime,
        ),
      );
      setClockOutTime(
        getTimePickerValue(
          updateTimesheet.clockOutTime || updateTimesheet.proposedClockOutTime,
          job.days?.endTime,
        ),
      );

      const breaks = updateTimesheet.breaks?.length
        ? updateTimesheet.breaks
        : updateTimesheet.proposedBreaks || [];
      const totalMinutes = breaks.reduce((sum, b) => {
        const durationMs = parseInt(b.duration || '0', 10);
        if (Number.isNaN(durationMs)) {
          return sum;
        }
        return sum + millisecondsToMinutes(durationMs);
      }, 0);
      setBreakTimeMinutes(String(Math.round(totalMinutes)));
    }
  }, [
    isOpen,
    updateTimesheet,
    getTimePickerValue,
    isOvernightShift,
    job.days?.startTime,
    job.days?.endTime,
  ]);

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-y-auto rounded-2xl bg-white px-6 py-12 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="flex items-center justify-center pb-5 text-lg leading-6 font-medium text-gray-900"
                >
                  <span className="py-2 text-lg font-medium text-gray-900">
                    {editAction === 'propose'
                      ? 'Edit Timesheet'
                      : 'Resolve Timesheet'}
                  </span>
                </DialogTitle>

                <UpdateTimesheetModal
                  isOpen={isResolveTimesheetConfirmationOpen}
                  onClose={() => setIsResolveTimesheetConfirmationOpen(false)}
                  updateTimesheet={updateTimesheet || null}
                  finalClockInTime={
                    resolvedTimesheetInput.clockInTimestamp || ''
                  }
                  finalClockOutTime={
                    resolvedTimesheetInput.clockOutTimestamp || ''
                  }
                  finalBreakTime={resolvedTimesheetInput.proposedBreaks || null}
                  confirmationVariant={
                    editAction === 'propose' ? 'propose' : 'resolve'
                  }
                  onResolveTimesheet={async () => {
                    await onResolveTimesheetDispute(resolvedTimesheetInput);
                    setIsResolveTimesheetConfirmationOpen(false);
                    onClose();
                  }}
                  onProposeTimesheet={async () => {
                    await onProposeTimesheetChanges(resolvedTimesheetInput);
                    setIsResolveTimesheetConfirmationOpen(false);
                    onClose();
                  }}
                />

                <div className="flex flex-col space-y-8">
                  <div className="flex items-center justify-between">
                    <span>Logged time</span>
                    <span className="font-medium text-orange-500">
                      {clockInTimestamp && clockOutTimestamp
                        ? `${formatTime(clockInTimestamp, job)} - ${formatTime(clockOutTimestamp, job)} (${timesheetDuration})`
                        : '—'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-in date</span>
                        <SingleDatePicker
                          value={clockInDate}
                          onChange={handleClockInDateChange}
                          minDate={jobCalendarBounds.min}
                          maxDate={jobCalendarBounds.max}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-in time</span>
                        <div className="flex-1">
                          <TimePicker
                            value={clockInTime}
                            onChange={setClockInTime}
                            label=""
                            onBlur={() => {}}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-out date</span>
                        <SingleDatePicker
                          value={clockOutDate}
                          onChange={setClockOutDate}
                          minDate={dayjs(clockInDate).startOf('day').toDate()}
                          maxDate={clockOutDateMax}
                          isInvalid={isInvalidTimeRange}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-out time</span>
                        <div className="flex-1">
                          <TimePicker
                            value={clockOutTime}
                            onChange={setClockOutTime}
                            label=""
                            onBlur={() => {}}
                            isInvalid={isInvalidTimeRange}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {isInvalidTimeRange && (
                    <p
                      role="alert"
                      className="text-center text-sm text-red-600"
                    >
                      Clock-out must be after clock-in. For overnight shifts,
                      set clock-out to the next calendar day or adjust times.
                    </p>
                  )}

                  <div className="flex flex-col items-center justify-between gap-2">
                    <span>Break time (minutes)</span>
                    <div className="min-w-60">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={breakTimeMinutes}
                        aria-invalid={isBreakTooLong || undefined}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || value === '-') {
                            setBreakTimeMinutes('');
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0) {
                              setBreakTimeMinutes(value);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          if (
                            e.target.value === '' ||
                            isNaN(parseInt(e.target.value, 10))
                          ) {
                            setBreakTimeMinutes('0');
                          }
                        }}
                        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
                          isBreakTooLong
                            ? 'border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    {isBreakTooLong && (
                      <p
                        role="alert"
                        className="text-center text-sm text-red-600"
                      >
                        Break time must be shorter than the shift.
                      </p>
                    )}
                    <span className="text-xs text-gray-500">
                      Current:{' '}
                      {updateTimesheet
                        ? formatBreakTime(
                            updateTimesheet.breaks ||
                              updateTimesheet.proposedBreaks,
                          )
                        : '0 mins'}
                    </span>
                  </div>

                  <div className="mt-4 flex justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={invalidInput}
                      onClick={() => {
                        setIsResolveTimesheetConfirmationOpen(true);
                      }}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      Update timesheet
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditTimesheetModal;
